import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Wallet, Building2, Users, Landmark, Plus, Printer, Calculator,
  CheckCircle2, Clock, DollarSign, ArrowUpCircle, LogOut, X, Link2, FileText,
} from "lucide-react";

const money = (n) =>
  `RD$${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DENOMS = [2000, 1000, 500, 200, 100, 50, 25, 10, 5, 1];

// ──────────────────────────── CASH SESSIONS ───────────────────────────
export function CashTab() {
  const [current, setCurrent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [denoms, setDenoms] = useState({});
  const [acceptDiff, setAcceptDiff] = useState(false);
  const [depositVault, setDepositVault] = useState(false);

  const refresh = async () => {
    try {
      const cur = await api.get("/cash-sessions/current").catch(() => ({ data: null }));
      setCurrent(cur.data);
    } catch { setCurrent(null); }
    const list = await api.get("/cash-sessions?limit=20");
    setSessions(list.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const total = Object.entries(denoms).reduce((s, [d, q]) => s + Number(d) * Number(q || 0), 0);

  const doOpen = async () => {
    try {
      await api.post("/cash-sessions/open", { denominations: denoms, notes: "" });
      toast.success("Turno abierto");
      setOpenDialog(false); setDenoms({}); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doClose = async () => {
    try {
      await api.post(`/cash-sessions/${current.id}/close`, {
        denominations: denoms, accept_diff: acceptDiff, deposit_to_vault: depositVault,
      });
      toast.success(depositVault ? "Turno cerrado + depósito a bóveda" : "Turno cerrado");
      setCloseDialog(false); setDenoms({}); setAcceptDiff(false); setDepositVault(false); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      {!current ? (
        <div className="rounded-lg border p-6 bg-muted/30 text-center">
          <Wallet className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
          <p className="mb-4 text-muted-foreground">No tienes un turno abierto</p>
          <Button data-testid="btn-open-session" onClick={() => setOpenDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Abrir Turno
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border p-5 space-y-3" data-testid="current-session">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Turno abierto · {current.number}</div>
              <div className="text-2xl font-bold">{money(current.opening_balance)}</div>
            </div>
            <Button variant="destructive" data-testid="btn-close-session" onClick={() => setCloseDialog(true)}>
              Cerrar Turno
            </Button>
          </div>
          {current.live_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">Fondo inicial:</span> <b>{money(current.live_summary.opening_balance)}</b></div>
              <div><span className="text-muted-foreground">Ventas efectivo:</span> <b className="text-emerald-700 dark:text-emerald-400">+{money(current.live_summary.sales_cash)}</b> <span className="text-xs text-muted-foreground">({current.live_summary.sales_count})</span></div>
              <div><span className="text-muted-foreground">Ventas tarjeta/transf:</span> <b>{money(current.live_summary.sales_card + current.live_summary.sales_transfer)}</b></div>
              <div><span className="text-muted-foreground">Ingresos recibos:</span> <b className="text-emerald-700 dark:text-emerald-400">+{money(current.live_summary.receipts_cash)}</b> <span className="text-xs text-muted-foreground">({current.live_summary.receipts_count || 0})</span></div>
              <div className="col-span-2 pl-4 border-l-2 border-emerald-200 text-xs text-muted-foreground">
                └ CxC: {money(current.live_summary.receipts_cash_applied || 0)} · Otros: {money(current.live_summary.receipts_cash_misc || 0)}
              </div>
              <div><span className="text-muted-foreground">Egresos recibos:</span> <b className="text-rose-700 dark:text-rose-400">-{money(current.live_summary.payments_cash)}</b> <span className="text-xs text-muted-foreground">({current.live_summary.payments_count || 0})</span></div>
              <div><span className="text-muted-foreground">Pagos CxC migrada:</span> <b className="text-emerald-700 dark:text-emerald-400">+{money(current.live_summary.migration_payments_cash || 0)}</b></div>
              <div className="col-span-2 pl-4 border-l-2 border-rose-200 text-xs text-muted-foreground">
                └ CxP: {money(current.live_summary.payments_cash_applied || 0)} · Otros: {money(current.live_summary.payments_cash_misc || 0)}
              </div>
              <div className="col-span-2 md:col-span-4 bg-primary/5 border border-primary/20 rounded-md p-2 flex justify-between items-center">
                <span className="font-bold text-sm">Efectivo esperado en caja:</span>
                <span className="text-xl font-black" data-testid="net-cash-expected">{money(current.live_summary.net_cash_expected)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <h3 className="font-semibold mb-2">Historial de turnos</h3>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Cajero</th><th className="p-2 text-left">Apertura</th><th className="p-2 text-right">Fondo inicial</th><th className="p-2 text-right">Diferencia</th><th className="p-2">Status</th><th></th></tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s.id} className="border-t">
                  <td className="p-2">{s.number}</td>
                  <td className="p-2">{s.cashier_name}</td>
                  <td className="p-2">{s.opened_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="p-2 text-right">{money(s.opening_balance)}</td>
                  <td className={`p-2 text-right ${s.diff > 0 ? "text-emerald-600" : s.diff < 0 ? "text-rose-600" : ""}`}>{s.diff != null ? money(s.diff) : "—"}</td>
                  <td className="p-2 text-center"><Badge variant={s.status === "closed" ? "secondary" : "default"}>{s.status}</Badge></td>
                  <td className="p-2 text-right">
                    {s.status === "closed" && (
                      <>
                        <Button size="sm" variant="ghost" data-testid={`btn-zreport-${s.id}`} title="Z-Report" onClick={() => window.open(`${api.defaults.baseURL}/cash-sessions/${s.id}/z-report.pdf`, "_blank")}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`btn-cashflow-${s.id}`} title="Cash Flow detallado" onClick={() => window.open(`${api.defaults.baseURL}/cash-sessions/${s.id}/cashflow.pdf`, "_blank")}>
                          <FileText className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!sessions.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sin turnos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={openDialog || closeDialog} onOpenChange={() => { setOpenDialog(false); setCloseDialog(false); setDenoms({}); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{openDialog ? "Abrir turno" : "Cerrar turno"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {DENOMS.map(d => (
              <div key={d} className="flex items-center gap-2">
                <Label className="w-20">RD${d.toLocaleString()}</Label>
                <Input type="number" min="0" data-testid={`denom-${d}`} value={denoms[d] || ""} onChange={e => setDenoms({ ...denoms, [d]: e.target.value })} />
              </div>
            ))}
            <div className="pt-2 flex justify-between font-bold border-t"><span>Total:</span> <span>{money(total)}</span></div>
            {closeDialog && (
              <>
                <label className="flex gap-2 items-center text-sm pt-2">
                  <input type="checkbox" checked={acceptDiff} onChange={e => setAcceptDiff(e.target.checked)} data-testid="accept-diff" />
                  Aceptar diferencia si existe
                </label>
                <label className="flex gap-2 items-start text-sm pt-1">
                  <input type="checkbox" checked={depositVault} onChange={e => setDepositVault(e.target.checked)} data-testid="deposit-vault" className="mt-0.5" />
                  <span>
                    <b>Depositar a bóveda / tesorería</b>
                    <br/><span className="text-xs text-muted-foreground">Genera asiento DB <code>1104 Caja Fuerte</code> / CR <code>1101 Caja</code> por el total contado. Útil al final del día cuando entregas el efectivo al administrador.</span>
                  </span>
                </label>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={openDialog ? doOpen : doClose} data-testid={openDialog ? "confirm-open" : "confirm-close"}>
              {openDialog ? "Abrir" : "Cerrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────── FIXED ASSETS ────────────────────────────
export function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [depDialog, setDepDialog] = useState(false);
  const [form, setForm] = useState({ category: "furniture", useful_life_years: 5, residual_value: 0 });
  const [period, setPeriod] = useState("");
  const [preview, setPreview] = useState(null);

  const refresh = async () => {
    const r = await api.get("/fixed-assets");
    setAssets(r.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const doCreate = async () => {
    try {
      await api.post("/fixed-assets", { ...form, acquisition_cost: Number(form.acquisition_cost), residual_value: Number(form.residual_value || 0), useful_life_years: Number(form.useful_life_years) });
      toast.success("Activo creado"); setAddDialog(false); setForm({ category: "furniture", useful_life_years: 5, residual_value: 0 }); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doPreview = async () => {
    try { const r = await api.get(`/fixed-assets/depreciate/preview/${period}`); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doRun = async () => {
    try {
      const r = await api.post("/fixed-assets/depreciate", { period });
      toast.success(`Depreciación ejecutada: ${r.data.processed.length} activos, ${money(r.data.total)}`);
      setDepDialog(false); setPreview(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <Button onClick={() => setAddDialog(true)} data-testid="btn-new-asset"><Plus className="w-4 h-4 mr-2" /> Nuevo Activo</Button>
        <Button variant="outline" onClick={() => setDepDialog(true)} data-testid="btn-run-depreciation"><Calculator className="w-4 h-4 mr-2" /> Depreciar Mes</Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr><th className="p-2 text-left">Código</th><th className="p-2 text-left">Nombre</th><th className="p-2">Categoría</th><th className="p-2 text-right">Costo</th><th className="p-2 text-right">Dep. mensual</th><th className="p-2 text-right">Vida (años)</th><th className="p-2">Status</th></tr>
          </thead>
          <tbody>
            {assets.map(a => (
              <tr key={a.id} className="border-t">
                <td className="p-2 font-mono">{a.code}</td>
                <td className="p-2">{a.name}</td>
                <td className="p-2 text-center"><Badge variant="outline">{a.category}</Badge></td>
                <td className="p-2 text-right">{money(a.acquisition_cost)}</td>
                <td className="p-2 text-right">{money(a.monthly_depreciation)}</td>
                <td className="p-2 text-right">{a.useful_life_years}</td>
                <td className="p-2 text-center"><Badge variant={a.status === "active" ? "default" : "secondary"}>{a.status}</Badge></td>
              </tr>
            ))}
            {!assets.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sin activos</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nuevo activo</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Código</Label><Input data-testid="asset-code" value={form.code || ""} onChange={e => setForm({ ...form, code: e.target.value })} /></div>
          <div><Label>Categoría</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="furniture">Mobiliario</SelectItem>
                <SelectItem value="computer">Computadora</SelectItem>
                <SelectItem value="vehicle">Vehículo</SelectItem>
                <SelectItem value="other">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Nombre</Label><Input data-testid="asset-name" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Fecha adquisición</Label><Input type="date" data-testid="asset-date" value={form.acquisition_date || ""} onChange={e => setForm({ ...form, acquisition_date: e.target.value })} /></div>
          <div><Label>Costo (RD$)</Label><Input type="number" data-testid="asset-cost" value={form.acquisition_cost || ""} onChange={e => setForm({ ...form, acquisition_cost: e.target.value })} /></div>
          <div><Label>Valor residual</Label><Input type="number" data-testid="asset-residual" value={form.residual_value || ""} onChange={e => setForm({ ...form, residual_value: e.target.value })} /></div>
          <div><Label>Vida útil (años)</Label><Input type="number" data-testid="asset-life" value={form.useful_life_years} onChange={e => setForm({ ...form, useful_life_years: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={doCreate} data-testid="btn-create-asset">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={depDialog} onOpenChange={setDepDialog}>
        <DialogContent><DialogHeader><DialogTitle>Depreciar período</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Período (YYYY-MM)</Label>
          <Input placeholder="2026-01" data-testid="dep-period" value={period} onChange={e => setPeriod(e.target.value)} />
          {preview && (
            <div className="p-3 bg-muted rounded text-sm">
              <div><b>{preview.assets.length}</b> activos a depreciar · Total: <b>{money(preview.total)}</b></div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={doPreview} data-testid="btn-preview-dep">Previsualizar</Button>
          <Button onClick={doRun} disabled={!preview} data-testid="btn-run-dep">Ejecutar</Button>
        </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────── PAYROLL ───────────────────────────────
export function PayrollTab() {
  const [tab, setTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [addEmp, setAddEmp] = useState(false);
  const [newRun, setNewRun] = useState(false);
  const [empForm, setEmpForm] = useState({ payment_frequency: "monthly", contract_type: "indefinido" });
  const [runForm, setRunForm] = useState({ frequency: "monthly" });
  // Dialogs de detalle
  const [sevEmp, setSevEmp] = useState(null);          // severance preview
  const [vacEmp, setVacEmp] = useState(null);          // vacation dialog

  const refresh = async () => {
    const [e, r] = await Promise.all([
      api.get("/payroll/employees"),
      api.get("/payroll/runs"),
    ]);
    setEmployees(e.data || []); setRuns(r.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const doAddEmp = async () => {
    try {
      await api.post("/payroll/employees", { ...empForm, base_salary: Number(empForm.base_salary) });
      toast.success("Empleado creado"); setAddEmp(false); setEmpForm({ payment_frequency: "monthly", contract_type: "indefinido" }); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doCreateRun = async () => {
    try {
      await api.post("/payroll/runs", runForm);
      toast.success("Nómina creada en borrador"); setNewRun(false); setRunForm({ frequency: "monthly" }); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doApprove = async (id) => {
    try { await api.post(`/payroll/runs/${id}/approve`); toast.success("Aprobada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doPay = async (id) => {
    try { await api.post(`/payroll/runs/${id}/pay`, { payment_method: "transferencia" }); toast.success("Pagada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doRegalia = async () => {
    const year = Number(prompt("¿Año de regalía?", new Date().getFullYear()));
    if (!year) return;
    try { await api.post("/payroll/regalia/runs", { year }); toast.success("Regalía creada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="employees" data-testid="tab-employees">Empleados</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Nóminas</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="space-y-3">
          <Button onClick={() => setAddEmp(true)} data-testid="btn-new-emp"><Plus className="w-4 h-4 mr-2" /> Nuevo Empleado</Button>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr><th className="p-2 text-left">Código</th><th className="p-2 text-left">Nombre</th><th className="p-2">Cédula</th><th className="p-2">Puesto</th><th className="p-2 text-right">Salario</th><th className="p-2">Frec.</th><th className="p-2">Status</th><th className="p-2 text-right">Acciones</th></tr>
              </thead>
              <tbody>
                {employees.map(e => (
                  <tr key={e.id} className="border-t">
                    <td className="p-2 font-mono">{e.code}</td>
                    <td className="p-2">{e.name}</td>
                    <td className="p-2 text-center text-xs">{e.cedula}</td>
                    <td className="p-2">{e.position}</td>
                    <td className="p-2 text-right">{money(e.base_salary)}</td>
                    <td className="p-2 text-center">{e.payment_frequency === "monthly" ? "Mensual" : "Quincenal"}</td>
                    <td className="p-2 text-center"><Badge variant={e.status === "active" ? "default" : "secondary"}>{e.status}</Badge></td>
                    <td className="p-2 text-right space-x-1">
                      {e.status === "active" && (
                        <>
                          <Button size="sm" variant="ghost" title="Vacaciones" onClick={() => setVacEmp(e)} data-testid={`btn-vac-${e.id}`}>
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Liquidar" onClick={() => setSevEmp(e)} data-testid={`btn-sev-${e.id}`}>
                            <LogOut className="w-4 h-4 text-rose-600" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {!employees.length && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin empleados</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="runs" className="space-y-3">
          <div className="flex gap-2">
            <Button onClick={() => setNewRun(true)} data-testid="btn-new-run"><Plus className="w-4 h-4 mr-2" /> Nueva Nómina</Button>
            <Button variant="outline" onClick={doRegalia} data-testid="btn-regalia"><ArrowUpCircle className="w-4 h-4 mr-2" /> Regalía</Button>
          </div>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr><th className="p-2 text-left">#</th><th className="p-2">Período</th><th className="p-2">Tipo</th><th className="p-2 text-right">Bruto</th><th className="p-2 text-right">Neto</th><th className="p-2">Emp.</th><th className="p-2">Status</th><th></th></tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2 font-mono">{r.number}</td>
                    <td className="p-2 text-center">{r.period}</td>
                    <td className="p-2 text-center"><Badge variant="outline">{r.kind || "regular"}</Badge></td>
                    <td className="p-2 text-right">{money(r.totals?.gross_period)}</td>
                    <td className="p-2 text-right font-bold">{money(r.totals?.net_pay)}</td>
                    <td className="p-2 text-center">{r.employee_count}</td>
                    <td className="p-2 text-center"><Badge>{r.status}</Badge></td>
                    <td className="p-2 text-right space-x-1">
                      {r.status === "draft" && <Button size="sm" variant="outline" onClick={() => doApprove(r.id)} data-testid={`btn-approve-${r.id}`}><CheckCircle2 className="w-3 h-3" /></Button>}
                      {r.status === "approved" && <Button size="sm" onClick={() => doPay(r.id)} data-testid={`btn-pay-${r.id}`}><DollarSign className="w-3 h-3" /></Button>}
                    </td>
                  </tr>
                ))}
                {!runs.length && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin nóminas</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={addEmp} onOpenChange={setAddEmp}>
        <DialogContent><DialogHeader><DialogTitle>Nuevo empleado</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Código</Label><Input data-testid="emp-code" value={empForm.code || ""} onChange={e => setEmpForm({ ...empForm, code: e.target.value })} /></div>
          <div><Label>Cédula</Label><Input data-testid="emp-cedula" value={empForm.cedula || ""} onChange={e => setEmpForm({ ...empForm, cedula: e.target.value })} /></div>
          <div className="col-span-2"><Label>Nombre</Label><Input data-testid="emp-name" value={empForm.name || ""} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} /></div>
          <div><Label>Puesto</Label><Input data-testid="emp-position" value={empForm.position || ""} onChange={e => setEmpForm({ ...empForm, position: e.target.value })} /></div>
          <div><Label>Fecha ingreso</Label><Input type="date" data-testid="emp-hire" value={empForm.hire_date || ""} onChange={e => setEmpForm({ ...empForm, hire_date: e.target.value })} /></div>
          <div><Label>Salario mensual</Label><Input type="number" data-testid="emp-salary" value={empForm.base_salary || ""} onChange={e => setEmpForm({ ...empForm, base_salary: e.target.value })} /></div>
          <div><Label>Frecuencia</Label>
            <Select value={empForm.payment_frequency} onValueChange={v => setEmpForm({ ...empForm, payment_frequency: v })}>
              <SelectTrigger data-testid="emp-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensual</SelectItem>
                <SelectItem value="biweekly">Quincenal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={doAddEmp} data-testid="btn-save-emp">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newRun} onOpenChange={setNewRun}>
        <DialogContent><DialogHeader><DialogTitle>Nueva nómina</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Período (YYYY-MM)</Label>
          <Input placeholder="2026-02" data-testid="run-period" value={runForm.period || ""} onChange={e => setRunForm({ ...runForm, period: e.target.value })} />
          <Label>Frecuencia</Label>
          <Select value={runForm.frequency} onValueChange={v => setRunForm({ ...runForm, frequency: v })}>
            <SelectTrigger data-testid="run-freq"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mensual</SelectItem>
              <SelectItem value="biweekly">Quincenal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter><Button onClick={doCreateRun} data-testid="btn-save-run">Crear borrador</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <SeveranceDialog employee={sevEmp} onClose={() => { setSevEmp(null); refresh(); }} />
      <VacationDialog employee={vacEmp} onClose={() => { setVacEmp(null); refresh(); }} />
    </div>
  );
}


// ─────────────── Severance (Prestaciones) ────────────────
function SeveranceDialog({ employee, onClose }) {
  const [form, setForm] = useState({ reason: "desahucio_empleador", paid_preaviso_days: 0 });
  const [preview, setPreview] = useState(null);
  const [opts, setOpts] = useState({
    include_preaviso: true, include_cesantia: true,
    include_vacation: true, include_regalia_proporcional: true,
  });

  useEffect(() => {
    if (!employee) { setPreview(null); return; }
    setForm({
      reason: "desahucio_empleador",
      paid_preaviso_days: 0,
      termination_date: new Date().toISOString().slice(0, 10),
    });
  }, [employee]);

  const doPreview = async () => {
    try {
      const r = await api.get(`/payroll/severance/preview/${employee.id}`, { params: {
        termination_date: form.termination_date, reason: form.reason,
        paid_preaviso_days: form.paid_preaviso_days,
      }});
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doCreate = async () => {
    try {
      await api.post("/payroll/severance", {
        employee_id: employee.id,
        termination_date: form.termination_date,
        reason: form.reason,
        paid_preaviso_days: Number(form.paid_preaviso_days || 0),
        ...opts,
      });
      toast.success("Liquidación creada en borrador. Aprueba y paga desde Nóminas.");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!employee) return null;

  return (
    <Dialog open={!!employee} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Liquidación · {employee.name}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {employee.position} · Ingreso {employee.hire_date} · Salario {money(employee.base_salary)}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Fecha salida</Label>
            <Input type="date" data-testid="sev-date" value={form.termination_date || ""} onChange={e => setForm({ ...form, termination_date: e.target.value })} />
          </div>
          <div>
            <Label>Motivo</Label>
            <Select value={form.reason} onValueChange={v => setForm({ ...form, reason: v })}>
              <SelectTrigger data-testid="sev-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="desahucio_empleador">Desahucio del empleador</SelectItem>
                <SelectItem value="mutuo_acuerdo">Mutuo acuerdo</SelectItem>
                <SelectItem value="desahucio_empleado">Desahucio del empleado</SelectItem>
                <SelectItem value="justa_causa">Justa causa (sin prestaciones)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Días preaviso ya dados</Label>
            <Input type="number" min="0" max="28" data-testid="sev-paid-days" value={form.paid_preaviso_days} onChange={e => setForm({ ...form, paid_preaviso_days: e.target.value })} />
          </div>
        </div>

        <Button variant="outline" onClick={doPreview} data-testid="btn-sev-preview">Calcular prestaciones</Button>

        {preview && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Antigüedad: <b>{preview.tenure_years} años</b> ({preview.tenure_days} días) · Salario diario: <b>{money(preview.daily_salary)}</b>
              {!preview.has_rights && <span className="text-rose-600 ml-2">⚠ Sin derecho a preaviso/cesantía (justa causa)</span>}
            </div>
            <div className="rounded border divide-y">
              {[
                ["preaviso", "Preaviso", `${preview.concepts.preaviso.days_due} días × ${money(preview.daily_salary)}`, "include_preaviso"],
                ["cesantia", "Cesantía", `${preview.concepts.cesantia.days_total} días (${preview.concepts.cesantia.days_per_year}/año × ${preview.tenure_years} años)`, "include_cesantia"],
                ["vacation_pending", "Vacaciones pendientes", `${preview.concepts.vacation_pending.balance_days} días`, "include_vacation"],
                ["regalia_proporcional", "Regalía proporcional", `${preview.concepts.regalia_proporcional.months} meses`, "include_regalia_proporcional"],
              ].map(([key, label, detail, optKey]) => {
                const c = preview.concepts[key];
                return (
                  <div key={key} className="flex items-center justify-between p-3 text-sm">
                    <label className="flex items-center gap-2 flex-1">
                      <input type="checkbox" checked={opts[optKey]} onChange={e => setOpts({ ...opts, [optKey]: e.target.checked })} data-testid={`chk-${optKey}`} />
                      <div>
                        <div className="font-medium">{label} {c.taxable && <Badge variant="outline" className="ml-1 text-[10px]">Grav. ISR/TSS</Badge>}</div>
                        <div className="text-xs text-muted-foreground">{detail}</div>
                      </div>
                    </label>
                    <span className="font-mono font-bold">{money(c.amount)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center p-3 bg-primary/5 rounded font-bold">
              <span>Total neto a pagar</span>
              <span className="text-xl">{money(preview.totals.net)}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={doCreate} disabled={!preview} data-testid="btn-sev-create">
            Crear liquidación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Vacation Grant + History ────────────────
function VacationDialog({ employee, onClose }) {
  const [balance, setBalance] = useState(null);
  const [grantDays, setGrantDays] = useState("");
  const [payDays, setPayDays] = useState("");

  useEffect(() => {
    if (!employee) { setBalance(null); return; }
    api.get(`/payroll/vacations/balance/${employee.id}`).then(r => setBalance(r.data));
  }, [employee]);

  const doGrant = async () => {
    try {
      await api.post("/payroll/vacations/grant", {
        employee_id: employee.id,
        days: grantDays ? Number(grantDays) : null,
      });
      toast.success("Días acreditados");
      const r = await api.get(`/payroll/vacations/balance/${employee.id}`);
      setBalance(r.data); setGrantDays("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doPay = async () => {
    try {
      await api.post("/payroll/vacations/pay", {
        employee_id: employee.id,
        days: Number(payDays),
      });
      toast.success("Pago de vacaciones creado (borrador en Nóminas)");
      const r = await api.get(`/payroll/vacations/balance/${employee.id}`);
      setBalance(r.data); setPayDays("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!employee) return null;
  return (
    <Dialog open={!!employee} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Vacaciones · {employee.name}</DialogTitle>
          <p className="text-sm text-muted-foreground">Ingreso {employee.hire_date}</p>
        </DialogHeader>

        {balance && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded bg-primary/5 border">
                <div className="text-xs text-muted-foreground">Balance actual</div>
                <div className="text-2xl font-bold">{balance.balance_days} días</div>
              </div>
              <div className="p-3 rounded bg-muted/50 border">
                <div className="text-xs text-muted-foreground">Sugerido por antigüedad</div>
                <div className="text-2xl font-bold">{balance.suggested_days_by_tenure} días</div>
                <div className="text-[11px] text-muted-foreground">(14 si 1-5 años, 18 si 5+)</div>
              </div>
            </div>

            <div className="rounded border p-3 space-y-2">
              <h4 className="font-semibold text-sm">Acreditar días</h4>
              <div className="flex gap-2">
                <Input type="number" placeholder="Dejar vacío para automático" data-testid="vac-grant-days" value={grantDays} onChange={e => setGrantDays(e.target.value)} />
                <Button onClick={doGrant} data-testid="btn-grant-vac">
                  <Plus className="w-4 h-4 mr-1" /> Acreditar
                </Button>
              </div>
            </div>

            <div className="rounded border p-3 space-y-2">
              <h4 className="font-semibold text-sm">Pagar vacaciones (crea borrador en Nóminas)</h4>
              <div className="flex gap-2">
                <Input type="number" min="1" placeholder={`Máx ${balance.balance_days}`} data-testid="vac-pay-days" value={payDays} onChange={e => setPayDays(e.target.value)} />
                <Button onClick={doPay} disabled={!payDays || Number(payDays) < 1 || Number(payDays) > balance.balance_days} data-testid="btn-pay-vac">
                  <DollarSign className="w-4 h-4 mr-1" /> Pagar
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Valor por día = salario / 23.83. Se aplica TSS/ISR normal.
              </p>
            </div>

            {balance.grants?.length > 0 && (
              <div className="rounded border max-h-40 overflow-auto">
                <div className="bg-muted/50 px-3 py-1.5 text-xs font-semibold">Historial de acreditaciones</div>
                <div className="divide-y text-xs">
                  {balance.grants.slice(0, 10).map((g, i) => (
                    <div key={i} className="px-3 py-1.5 flex justify-between">
                      <span>{g.as_of} · +{g.days_granted} días</span>
                      <span className="text-muted-foreground">balance: {g.balance_after}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter><Button variant="ghost" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────── BANK RECON ──────────────────────────────
export function BankTab() {
  const [banks, setBanks] = useState([]);
  const [statements, setStatements] = useState([]);
  const [addBank, setAddBank] = useState(false);
  const [upload, setUpload] = useState(false);
  const [bankForm, setBankForm] = useState({ account_code: "1103", currency: "DOP" });
  const [upForm, setUpForm] = useState({});
  const [file, setFile] = useState(null);
  const [detailStmt, setDetailStmt] = useState(null);  // statement detail

  const refresh = async () => {
    const [b, s] = await Promise.all([
      api.get("/bank-reconciliation/accounts"),
      api.get("/bank-reconciliation/statements"),
    ]);
    setBanks(b.data || []); setStatements(s.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const doAddBank = async () => {
    try {
      await api.post("/bank-reconciliation/accounts", { ...bankForm, opening_balance: Number(bankForm.opening_balance || 0) });
      toast.success("Cuenta creada"); setAddBank(false); setBankForm({ account_code: "1103", currency: "DOP" }); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doUpload = async () => {
    if (!file) { toast.error("Selecciona un CSV"); return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(
        `/bank-reconciliation/statements/upload?bank_account_id=${upForm.bank_account_id}&period=${upForm.period}&opening_balance=${upForm.opening_balance || 0}&closing_balance=${upForm.closing_balance || 0}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Estado cargado"); setUpload(false); setUpForm({}); setFile(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doAutoMatch = async (id) => {
    try {
      const r = await api.post(`/bank-reconciliation/statements/${id}/auto-match`);
      toast.success(`Auto-match: ${r.data.matched} ok, ${r.data.ambiguous} ambiguas, ${r.data.not_found} sin match`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doCloseStmt = async (id) => {
    try { await api.post(`/bank-reconciliation/statements/${id}/close`); toast.success("Estado cerrado"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold">Cuentas bancarias</h3>
          <Button size="sm" onClick={() => setAddBank(true)} data-testid="btn-new-bank"><Plus className="w-4 h-4 mr-2" /> Nueva Cuenta</Button>
        </div>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="p-2 text-left">Código</th><th className="p-2 text-left">Banco</th><th className="p-2">Cuenta</th><th className="p-2">GL</th><th className="p-2">Última conciliación</th></tr></thead>
            <tbody>
              {banks.map(b => (
                <tr key={b.id} className="border-t">
                  <td className="p-2 font-mono">{b.code}</td>
                  <td className="p-2">{b.bank_name}</td>
                  <td className="p-2 text-center font-mono">{b.account_number_masked}</td>
                  <td className="p-2 text-center">{b.account_code}</td>
                  <td className="p-2 text-center">{b.last_reconciled_at?.slice(0,10) || "—"}</td>
                </tr>
              ))}
              {!banks.length && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Sin cuentas bancarias</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold">Estados de cuenta</h3>
          <Button size="sm" onClick={() => setUpload(true)} data-testid="btn-upload-stmt" disabled={!banks.length}><Plus className="w-4 h-4 mr-2" /> Subir Estado</Button>
        </div>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="p-2 text-left">#</th><th className="p-2">Banco</th><th className="p-2">Período</th><th className="p-2 text-right">Líneas</th><th className="p-2 text-right">Conciliadas</th><th className="p-2">Status</th><th></th></tr></thead>
            <tbody>
              {statements.map(s => (
                <tr key={s.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => setDetailStmt(s)}>
                  <td className="p-2 font-mono">{s.number}</td>
                  <td className="p-2">{s.bank_name}</td>
                  <td className="p-2 text-center">{s.period}</td>
                  <td className="p-2 text-right">{s.total_lines}</td>
                  <td className="p-2 text-right">{s.matched_lines} / {s.pending_lines + s.matched_lines + s.ignored_lines}</td>
                  <td className="p-2 text-center"><Badge variant={s.status === "closed" ? "secondary" : s.status === "reconciled" ? "default" : "outline"}>{s.status}</Badge></td>
                  <td className="p-2 text-right space-x-1" onClick={e => e.stopPropagation()}>
                    {s.pending_lines > 0 && <Button size="sm" variant="outline" onClick={() => doAutoMatch(s.id)} data-testid={`btn-automatch-${s.id}`}>Auto-match</Button>}
                    {s.status === "reconciled" && <Button size="sm" onClick={() => doCloseStmt(s.id)} data-testid={`btn-close-${s.id}`}>Cerrar</Button>}
                  </td>
                </tr>
              ))}
              {!statements.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sin estados cargados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={addBank} onOpenChange={setAddBank}>
        <DialogContent><DialogHeader><DialogTitle>Nueva cuenta bancaria</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Código interno</Label><Input placeholder="BHD-001" data-testid="bank-code" value={bankForm.code || ""} onChange={e => setBankForm({ ...bankForm, code: e.target.value })} />
          <Label>Banco</Label><Input placeholder="Banco BHD" data-testid="bank-name" value={bankForm.bank_name || ""} onChange={e => setBankForm({ ...bankForm, bank_name: e.target.value })} />
          <Label>Número (enmascarado)</Label><Input placeholder="****1234" data-testid="bank-number" value={bankForm.account_number_masked || ""} onChange={e => setBankForm({ ...bankForm, account_number_masked: e.target.value })} />
          <Label>Cuenta contable</Label><Input placeholder="1103" data-testid="bank-glcode" value={bankForm.account_code} onChange={e => setBankForm({ ...bankForm, account_code: e.target.value })} />
        </div>
        <DialogFooter><Button onClick={doAddBank} data-testid="btn-save-bank">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={upload} onOpenChange={setUpload}>
        <DialogContent><DialogHeader><DialogTitle>Subir estado CSV</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Cuenta bancaria</Label>
          <Select value={upForm.bank_account_id || ""} onValueChange={v => setUpForm({ ...upForm, bank_account_id: v })}>
            <SelectTrigger data-testid="up-bank"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
            <SelectContent>
              {banks.map(b => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.code}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label>Período (YYYY-MM)</Label>
          <Input placeholder="2026-02" data-testid="up-period" value={upForm.period || ""} onChange={e => setUpForm({ ...upForm, period: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Saldo inicial</Label><Input type="number" data-testid="up-open" value={upForm.opening_balance || ""} onChange={e => setUpForm({ ...upForm, opening_balance: e.target.value })} /></div>
            <div><Label>Saldo final</Label><Input type="number" data-testid="up-close" value={upForm.closing_balance || ""} onChange={e => setUpForm({ ...upForm, closing_balance: e.target.value })} /></div>
          </div>
          <Label>Archivo CSV</Label>
          <Input type="file" accept=".csv" data-testid="up-file" onChange={e => setFile(e.target.files[0])} />
          <p className="text-xs text-muted-foreground">Formato: date, description, reference, amount, type (debit/credit)</p>
        </div>
        <DialogFooter><Button onClick={doUpload} data-testid="btn-up-upload">Cargar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <StatementDetailDialog stmt={detailStmt} onClose={() => { setDetailStmt(null); refresh(); }} />
    </div>
  );
}


// ─────────────── Statement Detail + Manual Match ────────────────
function StatementDetailDialog({ stmt, onClose }) {
  const [detail, setDetail] = useState(null);
  const [matching, setMatching] = useState(null);   // { line, candidates }

  const refresh = async () => {
    if (!stmt) { setDetail(null); return; }
    const r = await api.get(`/bank-reconciliation/statements/${stmt.id}`);
    setDetail(r.data);
  };
  useEffect(() => { refresh(); }, [stmt]);

  const findCandidates = async (line) => {
    try {
      const r = await api.get(`/bank-reconciliation/lines/${line.id}/candidates?window_days=10`);
      setMatching({ line, candidates: r.data.candidates });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const matchTo = async (jeId) => {
    try {
      await api.post(`/bank-reconciliation/lines/${matching.line.id}/match`, { journal_entry_id: jeId });
      toast.success("Línea matcheada"); setMatching(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ignoreLine = async (lineId) => {
    try { await api.post(`/bank-reconciliation/lines/${lineId}/ignore`); toast.success("Línea ignorada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const unmatch = async (lineId) => {
    try { await api.post(`/bank-reconciliation/lines/${lineId}/unmatch`); toast.success("Match removido"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!stmt) return null;
  return (
    <Dialog open={!!stmt} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Estado · {stmt.number} · {stmt.bank_name}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Período {stmt.period} · Saldo {money(stmt.opening_balance)} → {money(stmt.closing_balance)}
            {detail && <> · Créditos {money(detail.total_credits)} · Débitos {money(detail.total_debits)}</>}
          </p>
        </DialogHeader>

        {detail && (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr><th className="p-2">Fecha</th><th className="p-2 text-left">Descripción</th><th className="p-2">Ref</th><th className="p-2 text-right">Importe</th><th className="p-2">Tipo</th><th className="p-2">Status</th><th className="p-2">Acciones</th></tr>
              </thead>
              <tbody>
                {detail.lines.map(l => (
                  <tr key={l.id} className="border-t">
                    <td className="p-2 text-center">{l.date}</td>
                    <td className="p-2 text-xs">{l.description}</td>
                    <td className="p-2 text-center text-xs font-mono">{l.reference}</td>
                    <td className={`p-2 text-right font-mono ${l.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                      {l.type === "debit" ? "-" : ""}{money(l.amount)}
                    </td>
                    <td className="p-2 text-center text-xs">{l.type === "credit" ? "Abono" : "Cargo"}</td>
                    <td className="p-2 text-center">
                      <Badge variant={l.status === "matched" ? "default" : l.status === "ignored" ? "secondary" : "outline"}>
                        {l.status === "matched" ? `✓ ${l.matched_journal_entry_number || ""}` : l.status}
                      </Badge>
                    </td>
                    <td className="p-2 text-right space-x-1">
                      {l.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => findCandidates(l)} data-testid={`btn-find-${l.id}`}>
                            <Link2 className="w-3 h-3 mr-1" /> Match
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => ignoreLine(l.id)} data-testid={`btn-ignore-${l.id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {l.status === "matched" && (
                        <Button size="sm" variant="ghost" onClick={() => unmatch(l.id)} data-testid={`btn-unmatch-${l.id}`}>
                          Desmatch
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Submodal de candidatos */}
        <Dialog open={!!matching} onOpenChange={() => setMatching(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>Candidatos para matchear</DialogTitle></DialogHeader>
            {matching && (
              <div className="space-y-2">
                <div className="p-3 rounded bg-muted/50 text-sm">
                  <b>{matching.line.date}</b> · {matching.line.description} · {money(matching.line.amount)} ({matching.line.type})
                </div>
                {matching.candidates.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Sin asientos candidatos en ±10 días. Crea el asiento desde Contabilidad e intenta de nuevo.
                  </p>
                ) : (
                  <div className="rounded border divide-y max-h-96 overflow-auto">
                    {matching.candidates.map(je => (
                      <div key={je.id} className="p-3 flex items-center justify-between hover:bg-muted/30">
                        <div className="flex-1">
                          <div className="font-mono text-xs">{je.number} · {je.date?.slice(0,10)}</div>
                          <div className="text-sm">{je.description}</div>
                          <div className="text-xs text-muted-foreground">Ref: {je.reference || "—"}</div>
                        </div>
                        <Button size="sm" onClick={() => matchTo(je.id)} data-testid={`btn-match-${je.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Matchear
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <DialogFooter><Button variant="ghost" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── HUB ───────────────────────────────
export default function AmeliaHub() {
  const [tab, setTab] = useState("cash");
  return (
    <PageContainer>
      <PageHeader
        title="Amelia"
        subtitle="Caja, Activos, Nómina y Bancos"
      />
      <Tabs value={tab} onValueChange={setTab} className="mt-4" data-testid="suite-hub">
        <TabsList>
          <TabsTrigger value="cash" data-testid="tab-cash"><Wallet className="w-4 h-4 mr-2" /> Caja Z/X</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><Building2 className="w-4 h-4 mr-2" /> Activos Fijos</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Users className="w-4 h-4 mr-2" /> Nómina</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank"><Landmark className="w-4 h-4 mr-2" /> Conciliación</TabsTrigger>
        </TabsList>
        <TabsContent value="cash" className="mt-6"><CashTab /></TabsContent>
        <TabsContent value="assets" className="mt-6"><AssetsTab /></TabsContent>
        <TabsContent value="payroll" className="mt-6"><PayrollTab /></TabsContent>
        <TabsContent value="bank" className="mt-6"><BankTab /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}
